import React, { createContext, useState, useEffect, useCallback } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import SessionTimeout from '../components/SessionTimeout';
import { auth, db } from '../firebase';

const AuthContext = createContext(null);
const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 godzina
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; // 5 minut

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [customClaims, setCustomClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(SESSION_TIMEOUT / 1000);

  // Nasłuchiwanie zmian stanu autentykacji
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Pobierz dodatkowe dane użytkownika z Firestore
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          console.log('Firestore Document Exists:', userDoc.exists());
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('Firestore User Data:', userData);
            console.log('User Role:', userData.role);
            
            // Ustawiamy rolę użytkownika i customClaims
            await user.getIdToken(true);
            const tokenResult = await user.getIdTokenResult(true);
            console.log('Token claims:', tokenResult.claims);
            setCustomClaims(tokenResult.claims);
            setUserRole(tokenResult.claims.role || userData.role);
            
            setCurrentUser({
              ...user,
              ...userData
            });
          } else {
            console.log('Document does not exist for user:', user.uid);
            console.log('Creating new user document with admin role');
            
            // Tworzymy nowy dokument użytkownika z rolą admin
            const userData = {
              email: user.email,
              role: 'admin',
              username: user.email.split('@')[0],
              firstName: '',
              lastName: '',
              displayName: user.displayName || user.email,
              defaultLocation: 'PAN',
              createdAt: new Date().toISOString()
            };

            try {
              await setDoc(doc(db, 'users', user.uid), userData);
              console.log('Successfully created user document with role:', userData.role);
              setUserRole('admin');
              setCurrentUser({
                ...user,
                ...userData
              });
            } catch (error) {
              console.error('Error creating user document:', error);
              setUserRole(null);
              setCurrentUser(user);
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserRole(null);
          setCurrentUser(user);
        }
      } else {
        console.log('No user logged in');
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);



  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      window.location.href = '/login';
    } catch (error) {
      console.error('Błąd podczas wylogowywania:', error);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Błąd podczas logowania:', error);
      throw error;
    }
  }, []);



  const startCountdown = useCallback(() => {
    if (!currentUser) return;

    setTimeLeft(SESSION_TIMEOUT / 1000);
    setShowWarning(false);

    const timeoutId = setTimeout(() => {
      logout();
    }, SESSION_TIMEOUT);

    const warningId = setTimeout(() => {
      setShowWarning(true);
      
      const intervalId = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(intervalId);
            logout();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      return () => clearInterval(intervalId);
    }, SESSION_TIMEOUT - WARNING_BEFORE_TIMEOUT);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(warningId);
    };
  }, [currentUser, logout]);

  const handleExtendSession = useCallback(() => {
    startCountdown();
  }, [startCountdown]);

  const handleCloseWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  const value = {
    currentUser: currentUser,
    userRole,
    login,
    logout,
    loading,
    isAdmin: userRole === 'admin' || customClaims?.admin === true,
    isMarketing: customClaims?.marketing === true
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && (
        <>
          <SessionTimeout
            showWarning={showWarning}
            timeLeft={timeLeft}
            onExtend={handleExtendSession}
            onClose={handleCloseWarning}
          />
          {children}
        </>
      )}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export default AuthProvider;
